// components/PaymentMethod.js
import React, { useState } from 'react';
import {
  Box,
  Button,
  Input,
  FormControl,
  FormLabel,
  Select,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useToast,
} from '@chakra-ui/react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  getPaymentMethods,
  createPaymentMethod,
  updatePaymentMethod,
  deletePaymentMethod,
} from '../../../services/paymentMethodService';
import CustomSpinner from '../../EndedAuctions/CustomSpinner';

const PaymentMethod = () => {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [editingPaymentMethod, setEditingPaymentMethod] = useState(null);
  const [formValues, setFormValues] = useState({
    type: '',
    description: '',
    accountNumber: '',
    address: '',
    iban: '',
  });

  const { data, isLoading, isError, error } = useQuery('paymentMethods', getPaymentMethods);

  const createMutation = useMutation(createPaymentMethod, {
    onSuccess: () => {
      queryClient.invalidateQueries('paymentMethods');
      toast({
        title: 'Payment Method created.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error creating Payment Method.',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    },
  });

  const updateMutation = useMutation(updatePaymentMethod, {
    onSuccess: () => {
      queryClient.invalidateQueries('paymentMethods');
      toast({
        title: 'Payment Method updated.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error updating Payment Method.',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    },
  });

  const deleteMutation = useMutation(deletePaymentMethod, {
    onSuccess: () => {
      queryClient.invalidateQueries('paymentMethods');
      toast({
        title: 'Payment Method deleted.',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error deleting Payment Method.',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 2000,
        isClosable: true,
      });
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormValues({ ...formValues, [name]: value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const paymentMethodData = {
      type: formValues.type,
      description: formValues.description,
      accountNumber: formValues.accountNumber,
      address: formValues.address,
      iban: formValues.iban,
    };
  
    if (editingPaymentMethod) {
      updateMutation.mutate({ id: editingPaymentMethod._id, data: paymentMethodData });
    } else {
      createMutation.mutate(paymentMethodData);
    }
  
    setFormValues({
      type: '',
      description: '',
      accountNumber: '',
      address: '',
      iban: '',
    });
    setEditingPaymentMethod(null);
  };
  
  const handleEdit = (paymentMethod) => {
    setEditingPaymentMethod({
      _id: paymentMethod._id,
      type: paymentMethod.type,
      description: paymentMethod.description,
      accountNumber: paymentMethod.accountNumber,
      address: paymentMethod.address,
      iban: paymentMethod.iban,
    });
  };
  


  const handleDelete = (id) => {
    deleteMutation.mutate(id);
  };

  if (isLoading) {
    return <CustomSpinner />;
  }

  if (isError) {
    return (
      <Alert status="error">
        <AlertIcon />
        <AlertTitle>Failed to load payment methods</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  return (
    <Box>
      <form onSubmit={handleSubmit}>
        <FormControl isRequired>
          <FormLabel>Type</FormLabel>
          <Select name="type" value={formValues.type} onChange={handleInputChange}>
            <option value="">Select type</option>
            <option value="mobile">Mobile</option>
            <option value="fawry">Fawry</option>

            <option value="bank">Bank</option>
            <option value="instapay">Instapay</option>
            <option value="wallet">Wallet</option>
          </Select>
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Description</FormLabel>
          <Input name="description" value={formValues.description} onChange={handleInputChange} />
        </FormControl>
        <FormControl isRequired>
          <FormLabel>Account Number</FormLabel>
          <Input name="accountNumber" value={formValues.accountNumber} onChange={handleInputChange} />
        </FormControl>
        {formValues.type === 'bank' && (
          <>
            <FormControl isRequired>
              <FormLabel>Address</FormLabel>
              <Input name="address" value={formValues.address} onChange={handleInputChange} />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>IBAN</FormLabel>
              <Input name="iban" value={formValues.iban} onChange={handleInputChange} />
            </FormControl>
          </>
        )}
        <Button type="submit" colorScheme="blue" mt="4">
          {editingPaymentMethod ? 'Update Payment Method' : 'Create Payment Method'}
        </Button>
      </form>

      <Box mt="8">
        {data?.map((paymentMethod) => (
          <Box key={paymentMethod?._id} borderWidth="1px" borderRadius="lg" p="4" mb="4">
            <p>Type: {paymentMethod?.type}</p>
            <p>Description: {paymentMethod?.description}</p>
            <p>Account Number: {paymentMethod?.accountNumber}</p>
            {paymentMethod?.type === 'bank' && (
              <>
                <p>Address: {paymentMethod?.address}</p>
                <p>IBAN: {paymentMethod?.iban}</p>
              </>
            )}
            {/* <Button onClick={() => handleEdit(paymentMethod)} colorScheme="yellow" mr="2">
              Edit
            </Button> */}
            <Button onClick={() => handleDelete(paymentMethod?._id)} colorScheme="red">
              Delete
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PaymentMethod;
